/**
 * CORE-338 — normalise a picked photo to a format the feed can actually show.
 *
 * The post-photo path used to presign ANY unsupported type as `image/jpeg`, so
 * an iPhone HEIC was stored under a `.jpg` key, served with `Content-Type:
 * image/jpeg`, and then rendered as a blank tile everywhere except Safari — no
 * error, no clue. Two such photos are live in a production post (master 6, post
 * 168): all three objects return 200 and two are `ftypheic` bytes.
 *
 * Where the conversion happens, per surface:
 *   • iOS   — the picker asks for the compatible representation, so the system
 *             hands us JPEG and this file is never reached.
 *   • web   — here, when the browser can decode the source at all (Safari does
 *             decode HEIC; Chrome and Firefox do not).
 *   • other — the backend, once CORE-677 lands; until then an undecodable file
 *             is refused with a message instead of published as a blank tile.
 */

import { unsupportedPhotoFormat } from './uploadLimits';
import { PhotoFormatError } from './errorMessages';

/** Decoders and canvas exist — i.e. we are on web, not in React Native. */
function canConvert(): boolean {
  return (
    typeof createImageBitmap === 'function' &&
    typeof document !== 'undefined' &&
    typeof document.createElement === 'function'
  );
}

/**
 * Re-encode `blob` as JPEG, or null when this environment cannot decode it.
 * Never throws: the caller decides what an un-convertible file means.
 */
export async function convertImageToJpeg(blob: Blob): Promise<Blob | null> {
  if (!canConvert()) return null;
  try {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((out) => resolve(out), 'image/jpeg', 0.92);
    });
  } catch {
    // HEIC in Chrome/Firefox lands here — nothing to do but report it upward.
    return null;
  }
}

/**
 * CORE-722 — the one place that decides whether a picked photo can be
 * published: pass it through, re-encode it, or refuse it with copy that says
 * what to do instead.
 *
 * Both callers now ask this BEFORE the photo is staged in the composer, not
 * only at publish. A HEIC used to attach happily and then sit there as an empty
 * tile — no browser but Safari can decode one, so the preview never appeared —
 * and the author found out something was wrong only after writing the caption
 * and pressing «Опубликовать», by which point the reason had been rewritten
 * twice into «проверьте введённые данные». Refusing at pick time puts the
 * message next to the file that caused it, while it can still be swapped.
 *
 * `declaredType` is the picker's MIME (an empty Blob type is common on web);
 * it wins over the blob's own when present.
 */
export async function preparePostPhoto(
  blob: Blob,
  declaredType?: string | null,
): Promise<Blob> {
  const reason = unsupportedPhotoFormat(declaredType || blob.type);
  if (!reason) return blob;

  const jpeg = await convertImageToJpeg(blob);
  if (!jpeg) throw new PhotoFormatError(reason);
  return jpeg;
}
